import isEmpty from 'lodash/isEmpty';
import {
  Agreement,
  SimulationStatusEnum
} from 'storage/agreements/agreements.model';
import {
  Notification,
  NotificationDefinition,
  NotificationSeverity,
  RepaymentNotification,
  SEVERITY_TO_VARIANT_CONFIG
} from 'storage/notifications/notifications.model';
import {
  ARRAY_WITH_ONE_ELEMENT_LENGTH,
  Constant,
  EMPTY_ARRAY_LENGTH
} from 'utils/consts';
import getDifferenceInDays from 'utils/getDifferenceInDays';
import getProductParametersValues from 'pages/Dashboard/utils/getProductParametersValues';
import getInstallmentsToNotify from 'pages/Dashboard/utils/getInstallmentsData/getInstallmentsToNotify';
import getInstallmentsDays from 'pages/Dashboard/utils/getInstallmentsData/getInstallmentsDays';

const TWO_WEEKS_PERIOD = 15;

const createPaymentNotification = (
  name: NotificationDefinition,
  severity: NotificationSeverity,
  contractId: number,
  daysUntilDueDate: number,
  paymentAmount: number,
  currentDebtAmount: number,
  currency: string,
  createdAt: Date
): Notification =>
  ({
    name,
    severity,
    type: 'PAYMENT',
    createdAt,
    displayed: false,
    messageKey: name,
    context: {
      contractId,
      daysUntilDueDate,
      paymentAmount: { amount: paymentAmount, currency },
      currentDebtAmount: { amount: currentDebtAmount, currency },
      // in mapping scenario it's an agreement id
      sourceContractId: contractId
    },
    variant: SEVERITY_TO_VARIANT_CONFIG[severity]
  } as unknown as Notification);

const createRepaymentNotification = (
  contractId: number,
  paymentAmount: number,
  currency: string,
  createdAt: string
): RepaymentNotification =>
  ({
    name: 'REPAYMENT',
    severity: 'SUCCESS',
    type: 'REPAYMENT',
    createdAt,
    displayed: false,
    messageKey: 'REPAYMENT',
    context: {
      contractId,
      paymentAmount: { amount: paymentAmount, currency },
      sourceContractId: contractId.toString(),
      postingDate: createdAt
    },
    variant: SEVERITY_TO_VARIANT_CONFIG.SUCCESS
  } as unknown as RepaymentNotification);

export const mapAgreementToNotifications = (
  agreement: Agreement
): Notification[] => {
  const { generalData } = agreement;
  const { productData, lastSettledPayment } = generalData;
  const {
    waitingPaymentsAmountByCurrency,
    overduePaymentsAmount,
    totalDebtAmountByCurrency,
    currency,
    agreementId
  } = generalData;
  const {
    productParameters,
    installments,
    simulationStatus,
    prerequisitePaymentLeft
  } = productData;

  const productParams = getProductParametersValues(productParameters);
  const {
    prerequisitePaymentAmount,
    prerequisitePaymentDate,
    nextInstallmentAmount,
    installmentsAmount,
    nextInstallmentPayment,
    firstInstallmentPayment,
    installmentsCount,
    payoffDay
  } = productParams;

  const notifications: Notification[] = [];
  const createdAt = new Date();

  if (lastSettledPayment) {
    notifications.push(
      createRepaymentNotification(
        agreementId,
        lastSettledPayment.amount,
        lastSettledPayment.currency,
        lastSettledPayment.date
      )
    );
  }

  const isPrerequisitePayment =
    Number(prerequisitePaymentLeft) > 0 &&
    (simulationStatus ===
      SimulationStatusEnum.WAITING_TO_SATISFY_PREREQUISITES ||
      !!(
        prerequisitePaymentDate &&
        prerequisitePaymentAmount &&
        simulationStatus === SimulationStatusEnum.CONFIRMED
      ));
  const isInstallmentPlan = !isPrerequisitePayment && !!installmentsCount;

  const waitingPLN = waitingPaymentsAmountByCurrency?.PLN || 0;
  const totalDebt = totalDebtAmountByCurrency[currency] || 0;

  if (isPrerequisitePayment) {
    if (prerequisitePaymentDate) {
      const days = getDifferenceInDays(prerequisitePaymentDate);
      const amount = Number(prerequisitePaymentAmount || 0);

      if (isEmpty(waitingPaymentsAmountByCurrency) || waitingPLN < amount) {
        let name: NotificationDefinition | null = null;
        let severity: NotificationSeverity = 'ALERT';

        if (days < Constant.zero) {
          name = 'PREREQUISITE_PAYMENT_OVERDUE';
        } else if (days === Constant.zero) {
          name = 'PREREQUISITE_PAYMENT_DUE_TODAY';
        } else if (days < TWO_WEEKS_PERIOD && days > Constant.zero) {
          name = 'PREREQUISITE_PAYMENT_DUE_SOON';
          severity = 'WARNING';
        }

        if (name) {
          notifications.push(
            createPaymentNotification(
              name,
              severity,
              agreementId,
              days,
              amount,
              totalDebt,
              currency,
              createdAt
            )
          );
        }
      }
    }
    return notifications;
  }
  const nextAmount = Number(nextInstallmentAmount || installmentsAmount || 0);
  if (isInstallmentPlan) {
    const nextPaymentDays = nextInstallmentPayment
      ? getDifferenceInDays(nextInstallmentPayment)
      : null;

    const isOverdue =
      nextPaymentDays !== null &&
      nextPaymentDays < Constant.zero &&
      (isEmpty(waitingPaymentsAmountByCurrency) || waitingPLN < nextAmount);

    if (isOverdue && installments && installments.length > EMPTY_ARRAY_LENGTH) {
      notifications.push(
        createPaymentNotification(
          'INSTALLMENT_PAYMENT_OVERDUE',
          'ALERT',
          agreementId,
          nextPaymentDays,
          nextAmount,
          totalDebt,
          currency,
          createdAt
        )
      );
    }

    let daysArr: number[] = [];
    if (installments && installments.length > EMPTY_ARRAY_LENGTH) {
      const { installments: installmentsToNotify } = getInstallmentsToNotify(
        installments,
        overduePaymentsAmount,
        waitingPaymentsAmountByCurrency
      );
      daysArr = getInstallmentsDays(installmentsToNotify);
    } else if (firstInstallmentPayment) {
      daysArr = [getDifferenceInDays(firstInstallmentPayment)];
    }

    const todayPayment = daysArr.filter((day) => day === Constant.zero);
    const futurePayments = daysArr.filter(
      (day) => day > Constant.zero && day < TWO_WEEKS_PERIOD
    );

    // Overdue

    const overduePayments = daysArr.filter((day) => day < Constant.zero);

    if (overduePayments.length > ARRAY_WITH_ONE_ELEMENT_LENGTH) {
      if (
        overduePaymentsAmount === null ||
        isEmpty(waitingPaymentsAmountByCurrency) ||
        waitingPLN < overduePaymentsAmount
      ) {
        notifications.push(
          createPaymentNotification(
            'INSTALLMENTS_PAYMENT_OVERDUE',
            'ALERT',
            agreementId,
            Math.min(...overduePayments),
            overduePaymentsAmount ?? nextAmount,
            totalDebt,
            currency,
            createdAt
          )
        );
      }
    } else if (overduePayments.length === ARRAY_WITH_ONE_ELEMENT_LENGTH) {
      if (
        overduePaymentsAmount === null ||
        isEmpty(waitingPaymentsAmountByCurrency) ||
        waitingPLN < overduePaymentsAmount
      ) {
        notifications.push(
          createPaymentNotification(
            'INSTALLMENT_PAYMENT_OVERDUE',
            'ALERT',
            agreementId,
            overduePayments[0],
            overduePaymentsAmount ?? nextAmount,
            totalDebt,
            currency,
            createdAt
          )
        );
      }
    }

    // Due today
    if (todayPayment.length === ARRAY_WITH_ONE_ELEMENT_LENGTH) {
      if (
        isEmpty(waitingPaymentsAmountByCurrency) ||
        (isOverdue ? nextAmount : 0) + nextAmount > waitingPLN
      ) {
        notifications.push(
          createPaymentNotification(
            'INSTALLMENT_PAYMENT_DUE_TODAY',
            'ALERT',
            agreementId,
            0,
            nextAmount,
            totalDebt,
            currency,
            createdAt
          )
        );
      }
    }

    // Due soon
    if (
      isEmpty(waitingPaymentsAmountByCurrency) ||
      (isOverdue ? nextAmount : 0) + nextAmount > waitingPLN
    ) {
      futurePayments.forEach((days) => {
        notifications.push(
          createPaymentNotification(
            'INSTALLMENT_PAYMENT_DUE_SOON',
            'WARNING',
            agreementId,
            days,
            nextAmount,
            totalDebt,
            currency,
            createdAt
          )
        );
      });
    }

    return notifications;
  }

  // Payoff
  if (payoffDay) {
    const days = getDifferenceInDays(payoffDay);

    if (isEmpty(waitingPaymentsAmountByCurrency) || waitingPLN < totalDebt) {
      let name: NotificationDefinition | null = null;
      let severity: NotificationSeverity = 'ALERT';

      if (days < Constant.zero) {
        name = 'ONE_TIME_PAYMENT_OVERDUE';
      } else if (days === Constant.zero) {
        name = 'ONE_TIME_PAYMENT_DUE_TODAY';
      } else if (days < TWO_WEEKS_PERIOD && days > Constant.zero) {
        name = 'ONE_TIME_PAYMENT_DUE_SOON';
        severity = 'WARNING';
      }

      if (name) {
        notifications.push(
          createPaymentNotification(
            name,
            severity,
            agreementId,
            days,
            totalDebt,
            totalDebt,
            currency,
            createdAt
          )
        );
      }
    }
  }

  return notifications;
};
